import React, { useState, useRef, useEffect } from 'react'
import { InformationCircleIcon } from '@heroicons/react/24/outline'

interface FieldHelpProps {
  content: string | React.ReactNode
  className?: string
}

const FieldHelp: React.FC<FieldHelpProps> = ({ content, className = '' }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState<'bottom-left' | 'bottom-right' | 'top-left' | 'top-right'>('bottom-left')
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({})
  const tooltipRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (isVisible && buttonRef.current) {
      const button = buttonRef.current
      const rect = button.getBoundingClientRect()
      
      // Calculate available space
      const spaceOnRight = window.innerWidth - rect.right
      const spaceOnLeft = rect.left
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      
      let newPosition: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right' = 'bottom-left'
      
      // Determine horizontal position based on available space
      if (spaceOnRight >= spaceOnLeft) {
        // More space on right - show on right side
        newPosition = spaceBelow >= 100 ? 'bottom-left' : 'top-left'
      } else {
        // More space on left - show on left side
        newPosition = spaceBelow >= 100 ? 'bottom-right' : 'top-right'
      }
      
      // Adjust for vertical space
      if (spaceBelow < 100 && spaceAbove >= 100) {
        if (newPosition === 'bottom-left') newPosition = 'top-left'
        if (newPosition === 'bottom-right') newPosition = 'top-right'
      }
      
      setPosition(newPosition)
      
      // Calculate width - be more generous with the width
      let maxWidth: number
      
      if (newPosition === 'bottom-left' || newPosition === 'top-left') {
        // Tooltip extends to the right - use most of available space
        maxWidth = Math.min(560, spaceOnRight - 8) // Use more of the available space
      } else {
        // Tooltip extends to the left - use most of available space
        maxWidth = Math.min(560, spaceOnLeft - 8) // Use more of the available space
      }
      
      // Ensure minimum width for readability - make it wider
      maxWidth = Math.max(480, maxWidth) // Increased minimum to 480px
      
      // Never exceed viewport width but be generous
      maxWidth = Math.min(maxWidth, window.innerWidth - 16) // Only 8px margin on each side
      
      setTooltipStyle({ 
        maxWidth: `${maxWidth}px`,
        width: `${maxWidth}px`, // Set explicit width to ensure it uses the full space
        minWidth: '480px' // Ensure minimum width
      })
    }
  }, [isVisible])

  const getTooltipClasses = () => {
    const baseClasses = 'absolute z-50 p-4 bg-gray-900 text-white text-sm rounded-lg shadow-lg pointer-events-none whitespace-normal leading-relaxed'
    
    switch (position) {
      case 'bottom-left':
        return `${baseClasses} mt-1 left-0`
      case 'bottom-right':
        return `${baseClasses} mt-1 right-0`
      case 'top-left':
        return `${baseClasses} mb-1 bottom-full left-0`
      case 'top-right':
        return `${baseClasses} mb-1 bottom-full right-0`
      default:
        return `${baseClasses} mt-1 left-0`
    }
  }

  const getArrowClasses = () => {
    const baseClasses = 'absolute w-2 h-2 bg-gray-900 transform rotate-45'
    
    switch (position) {
      case 'bottom-left':
        return `${baseClasses} -top-1 left-3`
      case 'bottom-right':
        return `${baseClasses} -top-1 right-3`
      case 'top-left':
        return `${baseClasses} -bottom-1 left-3`
      case 'top-right':
        return `${baseClasses} -bottom-1 right-3`
      default:
        return `${baseClasses} -top-1 left-3`
    }
  }

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onFocus={() => setIsVisible(true)}
        onBlur={() => setIsVisible(false)}
        className="inline-flex items-center text-gray-400 hover:text-blue-600 transition-colors ml-1"
        title=""
        aria-label="Field help"
      >
        <InformationCircleIcon className="h-4 w-4" />
      </button>
      
      {isVisible && (
        <div
          ref={tooltipRef}
          className={getTooltipClasses()}
          style={tooltipStyle}
        >
          <div>
            {typeof content === 'string' ? (
              <p>{content}</p>
            ) : (
              content
            )}
          </div>
          {/* Arrow */}
          <div className={getArrowClasses()}></div>
        </div>
      )}
    </div>
  )
}

export default FieldHelp
